import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Empty, Spin } from "antd";
import { GetInvoiceHistoryService } from "../../../Services/InvoiceService/InvoiceService";
import { InvoiceHistoryEntry } from "../../../Pages/Steps/3Inbox/InvoiceFormHistory/Invoiceformhistory.types";
import "./StatementHistory.css";

interface StatementHistoryProps {
  itemId?: number;
  entityType?: "Invoice" | "SupplierStatement";
}

const formatSupplierStatementTimestamp = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const datePart = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart}, ${timePart}`;
};

const getSupplierStatementAction = (entry: InvoiceHistoryEntry): string => {
  const actor = entry.performedBy || "System";
  const action = String(entry.actionType)
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase();
  const actionCode = action.replace(/\s+/g, "");

  if (
    entry.fieldChange &&
    (actionCode === "fieldedited" || /status/i.test(entry.fieldChange.fieldName))
  ) {
    return `${entry.fieldChange.fieldName} changed from "${entry.fieldChange.oldValue ?? "—"}" to "${entry.fieldChange.newValue ?? "—"}" by ${actor}`;
  }

  switch (actionCode) {
    case "submitted":
    case "uploaded":
      return `Supplier Statement uploaded${entry.method ? ` via ${entry.method}` : ""} by ${actor}`;
    case "processingstarted":
      return `Supplier Statement processing started by ${actor}`;
    case "extractionresultrecorded":
      return `Extraction result recorded${entry.fieldChange ? `: ${entry.fieldChange.newValue ?? entry.fieldChange.oldValue ?? entry.fieldChange.fieldName}` : ""} by ${actor}`;
    case "extractionfailed":
      return `Extraction failed - automatically moved from Processing to Inbox with To Review status. — ${actor}`;
    case "failedextraction":
      return `Extraction failed - automatically moved from Processing to Inbox with To Review status. — ${actor}`;
    case "automaticallymovedtoprocessingtoinbox":
    case "automaticallymovedfromprocessingtoinbox":
    case "movedtoprocessingtoinbox":
    case "movedfromprocessingtoinbox":
      return `Automatically moved from Processing to Inbox by ${actor}`;
    case "readystatusassigned":
    case "statusassignedready":
      return `Ready status assigned by ${actor}`;
    case "toreviewstatusassigned":
    case "statusassignedtoreview":
      return `To Review status assigned by ${actor}`;
    case "reconciled":
    case "markedasreconciled":
      return `Supplier Statement marked as Reconciled by ${actor}`;
    case "archived":
      return `Supplier Statement archived by ${actor}`;
    case "unarchived":
    case "unarchivedstatement":
      return `Supplier Statement unarchived by ${actor}`;
    case "exported":
    case "exportedtoexcel":
      return `Supplier Statement exported to Excel by ${actor}`;
    case "publishedmanual":
      return `Supplier Statement published manually by ${actor}`;
    case "publishedauto":
      return `Supplier Statement published automatically by ${actor}`;
    default:
      return `${action.charAt(0).toUpperCase()}${action.slice(1)} by ${actor}`;
  }
};

const StatementHistory: React.FC<StatementHistoryProps> = ({
  itemId: propItemId,
  entityType = "Invoice",
}) => {
  const selectedInvoiceData = useSelector((state: any) => state.selectedInvoiceData.selectedInvoiceData);
  const itemId = propItemId ?? selectedInvoiceData?.invoiceId;
  const [entries, setEntries] = useState<InvoiceHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!itemId) {
      setEntries([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    GetInvoiceHistoryService(itemId, entityType)
      .then((history) => {
        if (cancelled) return;
        const sorted = [...history].sort(
          (a, b) =>
            new Date(b.performedAt).getTime() -
            new Date(a.performedAt).getTime(),
        );
        setEntries(sorted);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [entityType, itemId]);

  if (loading) {
    return <div className="sh-centered"><Spin size="small" /></div>;
  }

  if (entries.length === 0) {
    return (
      <div className="sh-centered">
        <Empty
          description={
            entityType === "SupplierStatement"
              ? "No history available for this statement."
              : "No history available."
          }
        />
      </div>
    );
  }

  if (entityType === "SupplierStatement") {
    return (
      <div className="history-container">
        <div className="history-heading">RECENT ACTIVITY</div>
        <ul className="history-timeline">
          {entries.map((entry) => (
            <li key={entry.id} className="history-item">
              <span className="history-dot dot-purple" />
              <div className="history-item-content">
                <div className="history-item-text">
                  {getSupplierStatementAction(entry)}
                </div>
                <div className="history-item-time">
                  {formatSupplierStatementTimestamp(entry.performedAt)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const formatTimestamp = (iso: string): string => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleString([], {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTitle = (entry: InvoiceHistoryEntry): string => {
    switch (entry.actionType) {
      case "SUBMITTED": return "Added to Inbox";
      case "PUBLISHED_MANUAL":
      case "PUBLISHED_AUTO": return "Published";
      case "ARCHIVED": return "Archived";
      case "RECONCILED": return "Marked as Reconciled";
      case "UNARCHIVED": return "Unarchived";
      case "FIELD_EDITED": return "Field Edited";
      case "VIEWED": return "Viewed";
      case "PAGE_SPLIT": return "Document Split";
      case "MERGED": return "Merged";
      case "UNMERGED": return "Unmerged";
      default: return entry.actionType;
    }
  };

  return (
    <div className="sh-container">
      {entries.map((entry) => {
        const who = entry.performedBy || "System";
        const time = formatTimestamp(entry.performedAt);
        const subtitle =
          entry.actionType === "SUBMITTED" && entry.method
            ? `${entry.method} by ${who} — ${time}`
            : entry.actionType === "FIELD_EDITED" && entry.fieldChange
              ? `${entry.fieldChange.fieldName}: "${entry.fieldChange.oldValue ?? "—"}" → "${entry.fieldChange.newValue ?? "—"}" — ${who} — ${time}`
              : `${who} — ${time}`;

        return (
          <div key={entry.id} className="sh-item">
            <div className="sh-title">{getTitle(entry)}</div>
            <div className="sh-subtitle">{subtitle}</div>
          </div>
        );
      })}
    </div>
  );
};

export default StatementHistory;
